import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import clsx from 'clsx'
import { format } from 'date-fns'
import axios from 'axios'
import { datatableTypes } from '@/typings'
import Swal from 'sweetalert2'
import Tippy from '@tippyjs/react'
import { datatableActions } from './store'
import DatatableColStruct from './DatatableColStruct'
import { constants } from '@/react-components/datatable/datatable.constant'

type Props = {
	align?: datatableTypes.DatatableColAlign
	color?: string
	backgroundColor?: string
	dataKey: string
	type: datatableTypes.DatatableColType
	value: datatableTypes.DatatableDataType
	last?: boolean
	size?: number
	imageSettings?: []
	action?: string
	icon?: string
	label?: string
	filter?: datatableTypes.DatatableFilter[]
	filterKey?: string
	vAlign?: datatableTypes.DatatableColValign
	tooltip?: string
	mediaWidth: number
	showMoreEnabled: boolean
}

const MAX_VALUE_LENGTH = 40

const formatValue = (value: string | number, more: boolean) => {
	if (value === null) {
		return ''
	}
	if (typeof value === 'number') {
		return value
	}
	if (!more && value?.length > MAX_VALUE_LENGTH) {
		return `${value.substr(0, MAX_VALUE_LENGTH)}...`
	}
	return value
}

const DatatableCol = ({
	backgroundColor,
	color,
	dataKey,
	filterKey,
	label,
	type,
	value,
	imageSettings,
	last,
	size,
	action,
	icon,
	filter,
	align,
	vAlign,
	tooltip,
	mediaWidth,
	showMoreEnabled,
}: Props) => {
	const valueRef = useRef<HTMLElement>(null)
	const dispatch = useDispatch()

	const [showMore, setShowMore] = useState(false)
	const [more, setMore] = useState(false)

	const target = document.querySelector(`[${constants.selector.datatable}]`)
	const readMoreText = target ? target.getAttribute(constants.attributes.readMoreText) : ''
	const readLessText = target ? target.getAttribute(constants.attributes.readLessText) : ''

	useEffect(() => {
		setMore(false)
	}, [value])

	useEffect(() => {
		if (!valueRef.current) {
			return
		}
		setShowMore(valueRef.current.scrollWidth > valueRef.current.clientWidth)
	}, [valueRef, value])

	const handleAction = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		if (!action) {
			return
		}

		axios
			.request({
				url: action,
			})
			.then(() => {
				dispatch(datatableActions.getData())
			})
	}

	const handleMore = (v: boolean) => () => {
		setMore(v)
	}

	const [showTooltip, setShowTooltip] = useState(false)

	const checkColumnWidth = () => {
		if (!valueRef.current) return

		const div = valueRef.current as HTMLDivElement
		const isOverflow = div.offsetWidth < div.scrollWidth

		setShowTooltip(isOverflow)
		setShowMore(isOverflow)
	}

	useEffect(() => {
		checkColumnWidth()
	}, [valueRef, value])

	window.addEventListener('resize', checkColumnWidth)

	const handleFilter = (v: any) => () => {
		if (!filter || !filterKey) {
			return
		}

		const filtersByKey = filter.filter((item) => item.dataKey === filterKey || item.customDataKey === filterKey)
		const colFilter = filtersByKey[0]
		if (!colFilter) {
			return
		}

		dispatch(
			datatableActions.updateFilter({
				filter: {
					dataKey:
						typeof colFilter.customDataKey !== 'undefined' &&
						!!colFilter.customDataKey &&
						colFilter.customDataKey.length
							? dataKey
							: filterKey,
					type: colFilter.type,
				},
				col: dataKey,
				value: v,
			}),
		)
		dispatch(datatableActions.getData())
	}

	const showVideo = (url: string) => () => {
		Swal.fire({
			html: `<video width="100%" controls="true" playsinline><source src="${url}" type="video/mp4" /></video>`,
			showConfirmButton: false,
			showCloseButton: false,
			onRender: function () {
				const videoContainer = document.querySelector('.swal2-html-container')
				if (!videoContainer) return
				const video = videoContainer.getElementsByTagName('video')[0]
				if (!video) return
				video.load()
				video.play()
			},
			customClass: {
				popup: 'w-full md:w-6/12',
				content: 'p-0',
			},
		})
	}

	const colValue = (
		<>
			{(type === 'text' || type === 'number') && (
				<>
					<span
						ref={valueRef}
						onClick={handleFilter(value)}
						className={clsx({
							truncate: !showMoreEnabled && type === 'text',
							'text-primary-400 underline hover:no-underline cursor-pointer': !!filterKey,
						})}
					>
						{showMoreEnabled && formatValue(value as string | number, more)}
						{!showMoreEnabled && typeof value === 'string' && type === 'text' && (
							<Tippy content={value} key={dataKey} reference={valueRef.current}>
								<span>{value}</span>
							</Tippy>
						)}
						{!showMoreEnabled && typeof value !== 'string' && type === 'text' && <span>{value}</span>}
						{!showMoreEnabled && type === 'number' && <span>{value}</span>}
						{showMoreEnabled &&
							typeof value === 'string' &&
							type === 'text' &&
							!showMore &&
							value.length > MAX_VALUE_LENGTH && (
								<button
									onClick={handleMore(!more)}
									type="button"
									className="text-primary-400 underline hover:no-underline ml-2"
									aria-label={more ? readLessText : readMoreText}
								>
									{more ? readLessText : readMoreText}
								</button>
							)}
					</span>
				</>
			)}
			{type === 'link' && value}
			{type === 'date' && value && format(new Date(value as string), 'dd.MM.yy')}
			{type === 'icon' && icon && <i className={icon} style={{ color }} />}
			{type === 'image' && value && (
				<>
					<img
						loading={'lazy'}
						src={value as string}
						width={imageSettings['preview']['width']}
						height={imageSettings['preview']['height']}
						className={imageSettings['preview']['class']}
					/>
				</>
			)}
			{type === 'video' && value && (
				<>
					<button
						className="bg-primary text-white py-2 px-4 hover:text-primary hover:bg-white border hover:border-primary"
						onClick={showVideo(value as string)}
					>
						Show video
					</button>
				</>
			)}
			{type === 'html' && <div dangerouslySetInnerHTML={{ __html: value as string }} />}
			{type === 'struct' &&
				(value as datatableTypes.DatatableDataStructType[]).map((props) => <DatatableColStruct {...props} />)}
		</>
	)

	return (
		<div
			className={clsx({
				'w-full lg:table-cell py-1 px-4': true,
				'align-bottom': vAlign === 'bottom',
				'align-middle': vAlign === 'middle',
				'align-top': vAlign === 'top',
				'border-r border-white': !last,
				'lg:w-auto': !size,
			})}
			style={size && mediaWidth >= 1024 ? { width: `${size}px`, backgroundColor } : { backgroundColor }}
		>
			<div
				className={clsx({
					'flex items-center lg:h-full flex-wrap': true,
					'justify-start text-left': align === 'start',
					'justify-center text-center': align === 'center',
					'justify-end text-right': align === 'end',
				})}
			>
				{tooltip && (
					<Tippy content={tooltip} key={dataKey}>
						<div className="mr-2 lg:hidden font-bold">{label}:</div>
					</Tippy>
				)}
				{!tooltip && <div className="mr-2 lg:hidden font-bold">{label}:</div>}
				{action && (
					<a onClick={handleAction} href={action} className="text-primary-400 underline hover:no-underline">
						{colValue}
					</a>
				)}
				{!action && colValue}
			</div>
		</div>
	)
}

export default DatatableCol
